import { useAtom, useAtomValue } from 'jotai';
import { useMutation } from 'react-query';

import { attributeSettingsAtom, PicklistItemFormatted } from 'atons/AttributeManagement/atoms';
import { customerAtom } from 'atons/Customer/atoms';

import { queryClient } from 'react-query/queryClient';
import { queryKeys } from 'react-query/constants';
import { ReactQueryHookOption } from 'react-query/types';
import { useGetCustomFieldDetail } from 'react-query/queries';
import { useSetErrorMessage } from 'customHooks/useSetErrorMessage';
import { CustomFieldGroup, postCustomFieldPickList } from 'services/attributeManagementApis';
import CustomFieldsAdapter from 'adapters/CustomFieldsAdapter';
import User from 'domain/model/User';

const usePostCustomFieldPicklist = (options?: ReactQueryHookOption<CustomFieldGroup>) => {
    const [{ id: customFieldId }, setAttributeSettings] = useAtom(attributeSettingsAtom);
    const customer = useAtomValue(customerAtom);
    const onError = useSetErrorMessage();

    const { refetch: fetchCustomFieldDetail } = useGetCustomFieldDetail(customFieldId as number, {
        onSuccess: data => setAttributeSettings(CustomFieldsAdapter.toAttributeSetting(data)),
    });

    const getUserId = (): string => {
        const userInstance = User.getInstance();
        if (userInstance === null) throw new Error('There was an error while retrieving the user instance');
        return userInstance.getUserId() as string;
    };

    return useMutation(
        (payload: PicklistItemFormatted) =>
            postCustomFieldPickList(
                customer!.clientId,
                customFieldId,
                CustomFieldsAdapter.postNewPicklistItem(payload, getUserId())
            ),
        {
            onSuccess: async data => {
                if (options && options.onSuccess) options.onSuccess(data);
                await queryClient.invalidateQueries([queryKeys.getCustomFieldPicklist]);
                await queryClient.invalidateQueries([queryKeys.customFieldDetail]);
                fetchCustomFieldDetail();
            },
            onError: (error: any) => {
                onError(error);
                if (options && options.onError) options.onError(error);
            },
        }
    );
};

export default usePostCustomFieldPicklist;
